import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  Logging,
  renderEdit,
  ControlState,
  stringToDate,
  renderDate,
  ControlStyleClass,
  ControlValidation,
  settingContextObjectLookup,
  settingCustomerCustomField,
  SettingsContextObject,
  renderCheckbox,
  ConfirmationModal,
  Notification,
  API,
  NotificationData,
  ResponseError,
  UserContext,
  UserInfo,
  PermissionGroup,
  getEditPermission,
} from '@vcd-ecm-ui/Common';
import CustomerGeneralProps from './CustomerGeneralProps.interface';
import '../../customer-info.scss';

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlStyle: ControlStyleClass = null;

const controlValidation: ControlValidation = {
  // Empty Validation
};

const CustomerGeneral = ({ customer, gatewayEndPoint }: CustomerGeneralProps): JSX.Element => {
  Logging.Debug('Customer General');
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);

  const dispatch = useDispatch();

  const [customerCode, setCustomerCode] = React.useState(customer.code);
  const [customerName, setCustomerName] = React.useState(customer.name);
  const [parentCustomer, setParentCustomer] = React.useState(customer.parent ? customer.parent.code : null);
  const [startDate, setStartDate] = React.useState(stringToDate(customer.effectiveDate));
  const [endDate, setEndDate] = React.useState(stringToDate(customer.endDate));
  const [taxPayerName, setTaxPayerName] = React.useState(customer.taxpayerDefinition
    ? `${customer.taxpayerDefinition.taxPayerName} / ${customer.taxpayerDefinition.partitionName}`
    : null);
  const [customField1, setCustomField1] = React.useState(customer.customField1);
  const [customField2, setCustomField2] = React.useState(customer.customField2);
  const [customField3, setCustomField3] = React.useState(customer.customField3);
  const [customField4, setCustomField4] = React.useState(customer.customField4);
  const [customField5, setCustomField5] = React.useState(customer.customField5);
  const [customField6, setCustomField6] = React.useState(customer.customField6);
  const [customField7, setCustomField7] = React.useState(customer.customField7);
  const [customField8, setCustomField8] = React.useState(customer.customField8);
  const [customField9, setCustomField9] = React.useState(customer.customField9);
  const [customField10, setCustomField10] = React.useState(customer.customField10);
  const [removeFromSync, setRemoveFromSync] = React.useState(customer.excludeFromSync);

  const customFieldHeader1: string = settingContextObjectLookup(settingsContext.customers, 1, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader2: string = settingContextObjectLookup(settingsContext.customers, 2, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader3: string = settingContextObjectLookup(settingsContext.customers, 3, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader4: string = settingContextObjectLookup(settingsContext.customers, 4, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader5: string = settingContextObjectLookup(settingsContext.customers, 5, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader6: string = settingContextObjectLookup(settingsContext.customers, 6, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader7: string = settingContextObjectLookup(settingsContext.customers, 7, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader8: string = settingContextObjectLookup(settingsContext.customers, 8, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader9: string = settingContextObjectLookup(settingsContext.customers, 9, settingCustomerCustomField, 'Custom Field');
  const customFieldHeader10: string = settingContextObjectLookup(settingsContext.customers, 10, settingCustomerCustomField, 'Custom Field');

  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);
  const [notification, setNotification] = React.useState(null);

  // Make the API Call to Update the Remove from Sync for Customer
  const customerRemoveFromSync = (excludeFromSync: boolean) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Update the Remove from Sync for Customer
      await api.customer.excludeFromSyncCustomerV41(customer.uuid, excludeFromSync);

      setRemoveFromSync(excludeFromSync);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customer Update Error',
        description: `Unable to Update Customer '${customer.name}'.`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Remove from Sync Confirmation : OK
  const onOKConfirmation = (): void => {
    Logging.Debug('OK Confirmation');
    setIsConfirmationOpen(false);

    // Update the Remove from Sync for Customer
    dispatch(customerRemoveFromSync(!removeFromSync));
  };

  // Remove from Sync Confirmation : Cancel
  const onCancelConfirmation = (): void => {
    Logging.Debug('Cancel Confirmation');
    setIsConfirmationOpen(false);
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  const onRemoveFromSyncChange = (value: boolean): void => {
    Logging.Debug('Remove from Sync:', value);

    // Only show Confirmation Dialog if checkbox is checked. Not when unchecked
    if (value) {
      setIsConfirmationOpen(true);
    } else {
      setRemoveFromSync(value);

      // Update the Remove from Sync for Customer
      dispatch(customerRemoveFromSync(value));
    }
  };

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);

  // Get the Customers Edit Permission
  const permissionCustomersEdit: boolean = getEditPermission(userContext, PermissionGroup.Customers);

  const confirmationMessage = 'Selecting this will remove the record from syncing to the calc engine.';

  return (
    <>
      <div className="generalContainer">
        {renderEdit(25, 'customer-code', 'Customer Code', 'text', null, customerCode, setCustomerCode,
          controlStateDisabled, controlStyle, controlValidation)}
        {renderEdit(25, 'customer-name', 'Customer Name', 'text', null, customerName, setCustomerName,
          controlStateDisabled, controlStyle, controlValidation)}
        {renderEdit(25, 'parent-customer', 'Parent Customer Code', 'text', null,
          parentCustomer, setParentCustomer, controlStateDisabled, controlStyle, controlValidation)}
        {renderDate(25, 'start-date', 'Start Date', 'Select Start Date', startDate, setStartDate, null,
          controlStateDisabled, controlStyle, controlValidation)}
        {renderDate(25, 'end-date', 'End Date', 'Select End Date', endDate, setEndDate, null,
          controlStateDisabled, controlStyle, controlValidation)}
        {renderEdit(25, 'taxpayer-name', 'Taxpayer', 'text', null, taxPayerName, setTaxPayerName,
          controlStateDisabled, controlStyle, controlValidation)}
        {
          customFieldHeader1
          && (
            renderEdit(25, 'customfield-1', customFieldHeader1, 'text', null, customField1, setCustomField1, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader2
          && (
            renderEdit(25, 'customfield-2', customFieldHeader2, 'text', null, customField2, setCustomField2, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader3
          && (
            renderEdit(25, 'customfield-3', customFieldHeader3, 'text', null, customField3, setCustomField3, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader4
          && (
            renderEdit(25, 'customfield-4', customFieldHeader4, 'text', null, customField4, setCustomField4, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader5
          && (
            renderEdit(25, 'customfield-5', customFieldHeader5, 'text', null, customField5, setCustomField5, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader6
          && (
            renderEdit(25, 'customfield-6', customFieldHeader6, 'text', null, customField6, setCustomField6, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader7
          && (
            renderEdit(25, 'customfield-7', customFieldHeader7, 'text', null, customField7, setCustomField7, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader8
          && (
            renderEdit(25, 'customfield-8', customFieldHeader8, 'text', null, customField8, setCustomField8, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader9
          && (
            renderEdit(25, 'customfield-9', customFieldHeader9, 'text', null, customField9, setCustomField9, controlStateDisabled, controlStyle)
          )
        }
        {
          customFieldHeader10
          && (
            renderEdit(25, 'customfield-10', customFieldHeader10, 'text', null, customField10, setCustomField10, controlStateDisabled, controlStyle)
          )
        }
        <div className="field25 removeFromSync">
          {renderCheckbox('removeFromSync', 'Remove from Sync', removeFromSync, null,
            onRemoveFromSyncChange, permissionCustomersEdit ? controlStateEnabled : controlStateDisabled, 'customerCheckbox')}
        </div>
      </div>
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        heading="Are you sure you want to continue?"
        message={confirmationMessage}
        okButtonName="Continue"
        onOK={onOKConfirmation}
        cancelButtonName="Cancel"
        onCancel={onCancelConfirmation}
        confirmationType="Warning"
      />
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default CustomerGeneral;
